import React, { useState, useCallback } from 'react';
import { Edit, useForm } from '@refinedev/antd';
import { useCustom, useCustomMutation } from '@refinedev/core';
import {
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Collapse,
  Upload,
  Button,
  Card,
  Image,
  message,
  Popconfirm,
  Divider,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import {
  EquipmentCatalogDto,
  EquipmentCategory,
  EquipmentDifficultyLevel,
} from '@wellverse/shared-ts';
import { API_BASE_URL } from '../../providers/dataProvider';
import { EquipmentLanguageTabs, normalizeEquipmentTranslations } from './LanguageTabs';


export const EquipmentCatalogEdit: React.FC = () => {
  const { formProps, saveButtonProps, queryResult } = useForm<EquipmentCatalogDto>({
    resource: 'equipment-catalog',
  });
  const id = queryResult?.data?.data?.id;

  // Fetch reference images
  const { data: imagesData, refetch: refetchImages } = useCustom({
    url: `/equipment-catalog/${id}/images`,
    method: 'get',
    queryOptions: {
      enabled: !!id,
    } as any,
  });
  const images = imagesData?.data ?? [];

  const { mutateAsync: customMutate } = useCustomMutation();

  const [uploading, setUploading] = useState(false);

  const handleUpload = useCallback(
    async ({ file, onSuccess, onError }: any) => {
      const authToken = localStorage.getItem('access_token');
      const formData = new FormData();
      formData.append('file', file);
      setUploading(true);
      try {
        await axios.post(`${API_BASE_URL}/equipment-catalog/${id}/images`, formData, {
          headers: {
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
            'Content-Type': 'multipart/form-data',
          },
        });
        onSuccess?.(null);
        message.success('Image uploaded');
        refetchImages();
      } catch (err: any) {
        onError?.(err);
        message.error(err.response?.data?.message || 'Upload failed');
      } finally {
        setUploading(false);
      }
    },
    [id, refetchImages],
  );

  const handleDeleteImage = useCallback(
    async (imageId: string) => {
      try {
        await customMutate({
          url: `/equipment-catalog/${id}/images/${imageId}`,
          method: 'delete',
          values: {},
        });
        message.success('Image removed');
        refetchImages();
      } catch {
        message.error('Failed to remove image');
      }
    },
    [id, customMutate, refetchImages],
  );

  return (
    <Edit resource="equipment-catalog" saveButtonProps={saveButtonProps}>
      {/* Reference Images Gallery */}
      <Card
        title="Reference Images"
        size="small"
        style={{ marginBottom: 24 }}
        extra={
          <Upload customRequest={handleUpload} showUploadList={false} accept="image/*">
            <Button type="primary" icon={<PlusOutlined />} loading={uploading} size="small">
              Upload Image
            </Button>
          </Upload>
        }
      >
        {images.length === 0 ? (
          <div style={{ color: '#898989', textAlign: 'center', padding: '16px 0' }}>
            No reference images. Upload photos to improve scan matching.
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {images.map((img: any) => (
              <div
                key={img.id}
                style={{
                  position: 'relative',
                  width: 120,
                  height: 120,
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: '1px solid #e8e8e8',
                }}
              >
                <Image
                  src={img.imageUrl}
                  alt="Reference"
                  width={120}
                  height={120}
                  style={{ objectFit: 'cover' }}
                  preview={{ mask: 'View' }}
                />
                <Popconfirm
                  title="Delete this image?"
                  onConfirm={() => handleDeleteImage(img.id)}
                  okText="Delete"
                  cancelText="Cancel"
                >
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      background: 'rgba(255,255,255,0.85)',
                      borderRadius: 4,
                    }}
                  />
                </Popconfirm>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Edit Form */}
      <Form
        {...formProps}
        layout="vertical"
        onFinish={(values: any) =>
          formProps.onFinish?.({
            ...values,
            translations: normalizeEquipmentTranslations(values.translations, queryResult?.data?.data?.translations),
          })
        }
      >
        <EquipmentLanguageTabs />

        <Divider orientation="left" plain>
          Shared
        </Divider>

        <Form.Item label="Brand" name="brand" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Model" name="model" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Category" name="category" rules={[{ required: true }]}>
          <Select>
            {Object.values(EquipmentCategory).map((c) => (
              <Select.Option key={c} value={c}>{c}</Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="Difficulty Level" name="difficultyLevel">
          <Select>
            {Object.values(EquipmentDifficultyLevel).map((l) => (
              <Select.Option key={l} value={l}>{l}</Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="Target Muscle Groups" name="targetMuscleGroups">
          <Select mode="tags" placeholder="e.g. chest, triceps, shoulders" />
        </Form.Item>
        <Form.Item label="Tags" name="tags">
          <Select mode="tags" placeholder="e.g. compound, upper-body" />
        </Form.Item>
        <Form.Item
          label="Aliases"
          name="aliases"
          extra="Alternate names used when matching scans (e.g. Chest Push → Chest Press)"
        >
          <Select mode="tags" placeholder="e.g. Chest Push, 推胸" />
        </Form.Item>
        <Form.Item label="Image URLs" name="imageUrls">
          <Select mode="tags" placeholder="Paste image URLs and press Enter" />
        </Form.Item>
        <Form.Item label="Video Links" name="videoLinks">
          <Select mode="tags" placeholder="Paste video URLs and press Enter" />
        </Form.Item>

        <Collapse
          style={{ marginBottom: 24 }}
          items={[
            {
              key: 'workout',
              label: 'Suggested Workout',
              children: (
                <>
                  <Form.Item label="Sets" name={['suggestedWorkout', 'sets']}>
                    <InputNumber style={{ width: '100%' }} min={1} />
                  </Form.Item>
                  <Form.Item label="Reps" name={['suggestedWorkout', 'reps']}>
                    <Input placeholder="e.g. 8-12" />
                  </Form.Item>
                  <Form.Item label="Recommended Weight" name={['suggestedWorkout', 'recommendedWeight']}>
                    <Input placeholder="e.g. 15-20kg" />
                  </Form.Item>
                  <Form.Item label="Rest Between Sets" name={['suggestedWorkout', 'restBetweenSets']}>
                    <Input placeholder="e.g. 60-90 seconds" />
                  </Form.Item>
                  <Form.Item label="Frequency" name={['suggestedWorkout', 'frequency']}>
                    <Input placeholder="e.g. 2-3 times/week" />
                  </Form.Item>
                  <Form.Item label="Variations" name={['suggestedWorkout', 'variations']}>
                    <Select mode="tags" placeholder="e.g. Close grip, Wide grip" />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'formGuide',
              label: 'Form Guide',
              children: (
                <>
                  <Form.Item label="Setup" name={['formGuide', 'setup']}>
                    <Input.TextArea rows={2} />
                  </Form.Item>
                  <Form.Item label="Execution" name={['formGuide', 'execution']}>
                    <Input.TextArea rows={3} />
                  </Form.Item>
                  <Form.Item label="Common Mistakes" name={['formGuide', 'commonMistakes']}>
                    <Select mode="tags" />
                  </Form.Item>
                  <Form.Item label="Tips" name={['formGuide', 'tips']}>
                    <Select mode="tags" />
                  </Form.Item>
                </>
              ),
            },
          ]}
        />

        <Form.Item label="Active" name="isActive" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Edit>
  );
};
